import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FlaskConical, Play, PlayCircle, Copy, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertCircle, Loader2, ArrowLeft, Shield, RefreshCw } from 'lucide-react';
import { ScenarioMismatchWarning } from './ScenarioMismatchWarning';
import { detectComplexityFlags, detectScenarioMismatch } from '../../server/services/scenarioClassifier';
import { resolveResidenceForConsumers } from '../../server/services/rnrbResidenceFacts';
import type { IHTCalculation } from '../../types';

type PresetSummary = {
    id: string;
    name: string;
    description: string;
    tags: string[];
    hasExpected: boolean;
};

type CheckResult = { field: string; expected: number; actual: number | null; passed: boolean };
type RunResult = {
    preset: { id: string; name: string };
    mutations: Record<string, any> | null;
    result: {
        status: string;
        total_tax_due: number | null;
        breakdown: any;
        audit_trail?: Array<{ step: string; description: string; value: number | null; formula?: string }>;
        blockers: string[];
        confidence: any;
    };
    estateData?: { assets: any; profile: any };
    checks: { allPassed: boolean; checks: CheckResult[]; invalidated?: boolean; invalidReason?: string } | null;
};

type RunAllSummary = {
    summary: { total: number; passed: number; failed: number };
    results: Array<{
        id: string; name: string; status: string;
        total_tax_due: number | null; blockers: string[];
        checks: { allPassed: boolean; checks: CheckResult[]; invalidated?: boolean; invalidReason?: string } | null;
    }>;
};

const API = '/api/dev/test-estates';
const GOLDEN_API = '/api/dev/golden';

type GoldenFieldCheck = { field: string; expected: number | null; actual: number | null; passed: boolean; diff: number | null };
type GoldenCaseResult = {
    id: string; name: string; description: string; tags: string[];
    allPassed: boolean; totalChecks: number; passedChecks: number; failedChecks: number;
    checks: GoldenFieldCheck[];
};
type GoldenRunAll = {
    summary: { total: number; passed: number; failed: number; tolerance: number };
    results: GoldenCaseResult[];
};
const fmt = (n: number | null) => n === null ? '—' : n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });

// ── Mutation Buttons ────────────────────────────────────────────────

// ── Custom Override Definitions ──────────────────────────────────────
const OVERRIDES: { key: string; label: string; placeholder: string; type: 'number' | 'date' | 'percent' }[] = [
    { key: 'override_cash_injection', label: 'Cash injection (£)', placeholder: 'e.g. 1650000', type: 'number' },
    { key: 'add_gifts', label: 'Gift amount (£)', placeholder: 'e.g. 275000', type: 'number' },
    { key: 'add_bpr_asset', label: 'BPR asset (£)', placeholder: 'e.g. 500000', type: 'number' },
    { key: 'override_apr_amount', label: 'APR asset (£)', placeholder: 'e.g. 300000', type: 'number' },
    { key: 'override_property_value', label: 'Property value (£)', placeholder: 'e.g. 425000', type: 'number' },
    { key: 'override_deceased_share', label: 'Deceased share (%)', placeholder: 'e.g. 50', type: 'percent' },
    { key: 'override_date_of_death', label: 'Date of death', placeholder: 'YYYY-MM-DD', type: 'date' },
    { key: 'override_tnrb_percent', label: 'Transferred NRB (%)', placeholder: 'e.g. 100', type: 'percent' },
    { key: 'override_trnrb_percent', label: 'Transferred RNRB (%)', placeholder: 'e.g. 100', type: 'percent' },
];

const MUTATIONS: { key: string; label: string; hasValue?: boolean; defaultValue?: number }[] = [
    { key: 'add_gifts', label: '+£100k Gifts', hasValue: true, defaultValue: 100000 },
    { key: 'toggle_spouse_exemption', label: 'Toggle Spouse Exemption' },
    { key: 'toggle_transferred_nrb', label: 'Toggle Transferred NRB' },
    { key: 'toggle_transferred_rnrb', label: 'Toggle Transferred RNRB' },
    { key: 'switch_property_to_other', label: 'Property → Other (no RNRB)' },
    { key: 'push_over_2m', label: 'Push Over £2M' },
    { key: 'add_bpr_asset', label: '+£500k BPR Asset', hasValue: true, defaultValue: 500000 },
    { key: 'remove_bpr', label: 'Remove All BPR' },
    { key: 'remove_apr', label: 'Remove All APR' },
];

/**
 * Context-aware action label for toggle mutations.
 * Uses imperative wording ("Turn X Off") so the user reads what WILL happen,
 * not the current state — eliminating ambiguity at a glance.
 */
function getMutationLabel(key: string, baseLabel: string, presetData: any): string {
    if (!presetData?.data) return baseLabel;
    const profile = presetData.data.EstateProfile;

    switch (key) {
        case 'toggle_spouse_exemption': {
            const isCurrentlyOn = profile?.deceased?.maritalStatus === 'MARRIED_OR_CP';
            return isCurrentlyOn ? 'Turn Spouse Exemption Off' : 'Turn Spouse Exemption On';
        }
        case 'toggle_transferred_nrb': {
            const ps = profile?.iht_details?.predeceased_spouse;
            const isOn = ps?.exists && (ps?.unused_nrb_percent ?? 0) > 0;
            return isOn ? 'Turn Transferred NRB Off' : 'Turn Transferred NRB On';
        }
        case 'toggle_transferred_rnrb': {
            const ps = profile?.iht_details?.predeceased_spouse;
            const isOn = (ps?.unused_rnrb_percent ?? 0) > 0;
            return isOn ? 'Turn Transferred RNRB Off' : 'Turn Transferred RNRB On';
        }
        default:
            return baseLabel;
    }
}

/**
 * Lightweight client-side simulation of mutation effects on scenario data.
 * Mirrors the key state changes from server/routes/devTestEstates.ts applyMutations().
 * Does NOT run the IHT engine — only produces a data fingerprint for comparison.
 */
function simulateMutations(baseData: any, mutations: Record<string, any>): string {
    // Deep clone to avoid mutating the original
    const data = JSON.parse(JSON.stringify(baseData));
    const assets = data.EstateAssets || {};
    const profile = data.EstateProfile || {};

    if (mutations.add_gifts) {
        if (!assets.lifetime_gifts) assets.lifetime_gifts = [];
        assets.lifetime_gifts.push({ id: 'sim-gift', amount: mutations.add_gifts });
    }

    if (mutations.toggle_spouse_exemption) {
        if (profile.deceased?.maritalStatus === 'MARRIED_OR_CP') {
            profile.deceased.maritalStatus = 'NEVER_MARRIED';
            if (profile.family) profile.family.assets_passed_to_spouse = undefined;
        } else {
            if (!profile.deceased) profile.deceased = {};
            profile.deceased.maritalStatus = 'MARRIED_OR_CP';
            if (!profile.family) profile.family = {};
            profile.family.assets_passed_to_spouse = 'yes';
        }
    }

    if (mutations.toggle_transferred_nrb) {
        if (!profile.iht_details) profile.iht_details = {};
        const ps = profile.iht_details.predeceased_spouse || {};
        if (ps.exists && (ps.unused_nrb_percent ?? 0) > 0) {
            ps.exists = false; ps.unused_nrb_percent = 0;
        } else {
            ps.exists = true; ps.unused_nrb_percent = 100;
        }
        profile.iht_details.predeceased_spouse = ps;
    }

    if (mutations.toggle_transferred_rnrb) {
        if (!profile.iht_details) profile.iht_details = {};
        const ps = profile.iht_details.predeceased_spouse || {};
        if ((ps.unused_rnrb_percent ?? 0) > 0) {
            ps.unused_rnrb_percent = 0;
        } else {
            ps.exists = true; ps.unused_rnrb_percent = 100; ps.trnrb_claim = 'yes';
        }
        profile.iht_details.predeceased_spouse = ps;
    }

    if (mutations.switch_property_to_other) {
        // The residence is the shared Stage-1 QRI (never is_main_residence); an unresolved estate has none to switch.
        const qri = resolveResidenceForConsumers(assets.properties || [], profile.iht_details).qri;
        if (qri) {
            qri.deceased_share_passes_to = 'other';
            qri.joint_owner_relationship = 'other';
        }
    }

    if (mutations.push_over_2m) {
        const total = (assets.bank_accounts || []).reduce((s: number, b: any) => s + (b.estimated_value || 0), 0)
            + (assets.properties || []).reduce((s: number, p: any) => s + (p.estimated_value || 0), 0)
            + (assets.investments || []).reduce((s: number, i: any) => s + (i.estimated_value || 0), 0);
        if (total < 2100000) {
            if (!assets.bank_accounts) assets.bank_accounts = [];
            assets.bank_accounts.push({ id: 'sim-push', estimated_value: 2100000 - total });
        }
    }

    if (mutations.add_bpr_asset) {
        if (!assets.bpr_assets) assets.bpr_assets = [];
        assets.bpr_assets.push({ id: 'sim-bpr', estimated_value: mutations.add_bpr_asset, qualifies: true });
    }

    if (mutations.remove_bpr) {
        assets.bpr_assets = [];
    }

    if (mutations.remove_apr) {
        assets.apr_assets = [];
    }

    // Custom overrides (applied after quick mutations)
    if (mutations.override_cash_injection && Number(mutations.override_cash_injection) > 0) {
        if (!assets.bank_accounts) assets.bank_accounts = [];
        assets.bank_accounts.push({ id: 'sim-cash', estimated_value: Number(mutations.override_cash_injection) });
    }
    if (mutations.override_property_value && Number(mutations.override_property_value) > 0) {
        const prop = (assets.properties || [])[0];
        if (prop) prop.estimated_value = Number(mutations.override_property_value);
    }
    if (mutations.override_deceased_share != null && String(mutations.override_deceased_share) !== '') {
        const prop = (assets.properties || [])[0];
        if (prop) prop.deceased_share_percent = Number(mutations.override_deceased_share);
    }
    if (mutations.override_apr_amount && Number(mutations.override_apr_amount) > 0) {
        if (!assets.apr_assets) assets.apr_assets = [];
        assets.apr_assets.push({ id: 'sim-apr', estimated_value: Number(mutations.override_apr_amount) });
    }
    if (mutations.override_date_of_death) {
        if (!profile.deceased) profile.deceased = {};
        profile.deceased.date_of_death = mutations.override_date_of_death;
    }
    if (mutations.override_tnrb_percent != null && String(mutations.override_tnrb_percent) !== '') {
        if (!profile.iht_details) profile.iht_details = {};
        if (!profile.iht_details.predeceased_spouse) profile.iht_details.predeceased_spouse = {};
        profile.iht_details.predeceased_spouse.exists = true;
        profile.iht_details.predeceased_spouse.unused_nrb_percent = Number(mutations.override_tnrb_percent);
    }
    if (mutations.override_trnrb_percent != null && String(mutations.override_trnrb_percent) !== '') {
        if (!profile.iht_details) profile.iht_details = {};
        if (!profile.iht_details.predeceased_spouse) profile.iht_details.predeceased_spouse = {};
        profile.iht_details.predeceased_spouse.exists = true;
        profile.iht_details.predeceased_spouse.unused_rnrb_percent = Number(mutations.override_trnrb_percent);
    }

    // Return a stable fingerprint of the scenario-relevant data
    return JSON.stringify({ assets, profile });
}

/**
 * Computes which mutations are effective by comparing final state with
 * and without each mutation. A mutation is effective if removing it from
 * the set changes the final scenario state.
 */
function computeEffectiveMutations(
    presetData: any,
    activeMutations: Record<string, any>,
): Record<string, any> {
    if (!presetData?.data) return { ...activeMutations };
    const baseData = presetData.data;
    const activeKeys = Object.keys(activeMutations);
    if (activeKeys.length === 0) return {};

    // Compute the full mutated state with all active mutations
    const fullState = simulateMutations(baseData, activeMutations);
    // Also compute the base state (no mutations)
    const baseState = simulateMutations(baseData, {});

    // If nothing changed at all, no mutations are effective
    if (fullState === baseState) return {};

    const effective: Record<string, any> = {};
    for (const key of activeKeys) {
        // Compute state without this specific mutation
        const without: Record<string, any> = {};
        for (const k of activeKeys) {
            if (k !== key) without[k] = activeMutations[k];
        }
        const stateWithout = simulateMutations(baseData, without);

        // If removing this mutation changes the final state, it's effective
        if (stateWithout !== fullState) {
            effective[key] = activeMutations[key];
        }
    }

    return effective;
}

// ── Main Component ──────────────────────────────────────────────────

interface TestEstatesPageProps {
    onLoadIntoAudit?: (data: any) => void;
    onBack?: () => void;
}

export const TestEstatesPage: React.FC<TestEstatesPageProps> = ({ onLoadIntoAudit, onBack }) => {
    const [presets, setPresets] = useState<PresetSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [presetData, setPresetData] = useState<any>(null);
    const [runResult, setRunResult] = useState<RunResult | null>(null);
    const [runAllResult, setRunAllResult] = useState<RunAllSummary | null>(null);
    const [running, setRunning] = useState(false);
    const [runningAll, setRunningAll] = useState(false);
    const [expandedJson, setExpandedJson] = useState(false);
    const [expandedAudit, setExpandedAudit] = useState(true);
    const [activeMutations, setActiveMutations] = useState<Record<string, any>>({});
    const [overridesExpanded, setOverridesExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const [tagFilter, setTagFilter] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'presets' | 'golden'>('presets');
    const [goldenResults, setGoldenResults] = useState<GoldenRunAll | null>(null);
    const [goldenRunning, setGoldenRunning] = useState(false);
    const [goldenExpanded, setGoldenExpanded] = useState<string | null>(null);
    const [acceptingId, setAcceptingId] = useState<string | null>(null);

    // Compute effective mutations by comparing final states with/without each mutation
    const effectiveMutations = useMemo(() => {
        return computeEffectiveMutations(presetData, activeMutations);
    }, [activeMutations, presetData]);

    // Detect scenario mismatch when a test case has been mutated
    const scenarioMismatch = useMemo(() => {
        if (!runResult || !selectedId) return null;
        const preset = presets.find(p => p.id === selectedId);
        if (!preset) return null;
        const ihtCalc = runResult.result as unknown as IHTCalculation;
        const flags = detectComplexityFlags(ihtCalc, null, null);
        return detectScenarioMismatch(preset.tags, preset.name, flags);
    }, [runResult, selectedId, presets]);

    // Load presets on mount
    useEffect(() => {
        fetch(API).then(r => r.json()).then(d => {
            setPresets(d.presets);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    // Load full preset when selected
    useEffect(() => {
        if (!selectedId) { setPresetData(null); setRunResult(null); return; }
        fetch(`${API}/${selectedId}`).then(r => r.json()).then(d => setPresetData(d));
        setRunResult(null);
        setActiveMutations({});
    }, [selectedId]);

    const runPreset = useCallback(async () => {
        if (!selectedId) return;
        setRunning(true);
        try {
            const res = await fetch(`${API}/${selectedId}/run`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mutations: activeMutations }),
            });
            const data = await res.json();
            setRunResult(data);
        } finally {
            setRunning(false);
        }
    }, [selectedId, activeMutations]);

    const runAll = useCallback(async () => {
        setRunningAll(true);
        try {
            const res = await fetch(`${API}/run-all`, { method: 'POST' });
            const data = await res.json();
            setRunAllResult(data);
        } finally {
            setRunningAll(false);
        }
    }, []);

    const copyJson = useCallback(() => {
        if (!presetData) return;
        navigator.clipboard.writeText(JSON.stringify(presetData.data, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [presetData]);

    const toggleMutation = (key: string, defaultValue?: number) => {
        setActiveMutations(prev => {
            const next = { ...prev };
            if (next[key]) {
                delete next[key];
            } else {
                next[key] = defaultValue ?? true;
            }
            return next;
        });
    };

    const runGolden = useCallback(async () => {
        setGoldenRunning(true);
        try {
            const res = await fetch(`${GOLDEN_API}/run-all`, { method: 'POST' });
            const data = await res.json();
            setGoldenResults(data);
        } finally {
            setGoldenRunning(false);
        }
    }, []);

    const acceptGolden = useCallback(async (id: string) => {
        setAcceptingId(id);
        try {
            const res = await fetch(`${GOLDEN_API}/${id}/accept`, { method: 'POST' });
            const data = await res.json();
            // Re-run to show updated results
            if (data.newExpected) {
                await runGolden();
            }
        } finally {
            setAcceptingId(null);
        }
    }, [runGolden]);

    // Collect all tags
    const allTags = Array.from(new Set(presets.flatMap(p => p.tags))).sort();
    const filtered = tagFilter ? presets.filter(p => p.tags.includes(tagFilter)) : presets;

    if (loading) return <div className="p-8 text-center text-slate-400">Loading presets...</div>;

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                {onBack && (
                    <button onClick={onBack} className="p-1.5 rounded hover:bg-slate-100 text-slate-500">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                )}
                <FlaskConical className="w-6 h-6 text-indigo-600" />
                <div>
                    <h1 className="text-xl font-bold text-slate-800">Test Estate Generator</h1>
                    <p className="text-xs text-slate-400">Internal tool — load preset estates and run through the IHT engine</p>
                </div>
                <div className="ml-auto flex gap-2">
                    {activeTab === 'presets' && (
                        <button onClick={runAll} disabled={runningAll}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded hover:bg-indigo-700 disabled:opacity-50">
                            {runningAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                            Run All Presets
                        </button>
                    )}
                    {activeTab === 'golden' && (
                        <button onClick={runGolden} disabled={goldenRunning}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded hover:bg-amber-700 disabled:opacity-50">
                            {goldenRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                            Run Regression Check
                        </button>
                    )}
                </div>
            </div>

            {/* Tab bar */}
            <div className="flex gap-1 mb-4 border-b border-slate-200">
                <button onClick={() => setActiveTab('presets')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'presets'
                            ? 'border-indigo-600 text-indigo-700'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}>
                    <FlaskConical className="w-3.5 h-3.5 inline mr-1.5" />
                    Test Estates
                </button>
                <button onClick={() => setActiveTab('golden')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'golden'
                            ? 'border-amber-600 text-amber-700'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}>
                    <Shield className="w-3.5 h-3.5 inline mr-1.5" />
                    Golden Regression
                    {goldenResults && (
                        <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full font-bold ${
                            goldenResults.summary.failed === 0
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-red-100 text-red-700'
                        }`}>
                            {goldenResults.summary.failed === 0 ? `${goldenResults.summary.passed} OK` : `${goldenResults.summary.failed} FAIL`}
                        </span>
                    )}
                </button>
            </div>

            {/* ── Golden Regression Tab ──────────────────────────── */}
            {activeTab === 'golden' && (
                <GoldenRegressionPanel
                    results={goldenResults}
                    running={goldenRunning}
                    expandedId={goldenExpanded}
                    acceptingId={acceptingId}
                    onRun={runGolden}
                    onToggleExpand={(id) => setGoldenExpanded(goldenExpanded === id ? null : id)}
                    onAccept={acceptGolden}
                />
            )}

            {/* ── Presets Tab ─────────────────────────────────────── */}
            {activeTab === 'presets' && <>

            {/* Run All Results */}
            {runAllResult && (
                <div className="mb-6 bg-white border rounded-lg shadow-sm p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <h2 className="font-bold text-sm text-slate-700">Batch Results</h2>
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                            {runAllResult.summary.passed} passed
                        </span>
                        {runAllResult.summary.failed > 0 && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                                {runAllResult.summary.failed} failed
                            </span>
                        )}
                    </div>
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-slate-400 border-b">
                                <th className="pb-1 font-semibold">Preset</th>
                                <th className="pb-1 font-semibold text-right">Tax Due</th>
                                <th className="pb-1 font-semibold text-center">Status</th>
                                <th className="pb-1 font-semibold text-center">Checks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runAllResult.results.map(r => (
                                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => { setSelectedId(r.id); setRunAllResult(null); }}>
                                    <td className="py-1.5 pr-3 text-slate-700">{r.name}</td>
                                    <td className="py-1.5 text-right font-mono text-slate-600">{fmt(r.total_tax_due)}</td>
                                    <td className="py-1.5 text-center">
                                        {r.blockers?.length > 0
                                            ? <AlertCircle className="w-3.5 h-3.5 text-amber-500 inline" />
                                            : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" />}
                                    </td>
                                    <td className="py-1.5 text-center">
                                        {r.checks === null ? <span className="text-slate-300">—</span>
                                            : r.checks.allPassed
                                                ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" />
                                                : <XCircle className="w-3.5 h-3.5 text-red-500 inline" />}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* ── Left: Preset List ──────────────────────────────── */}
                <div className="lg:col-span-1 space-y-3">
                    {/* Tag filter */}
                    <div className="flex flex-wrap gap-1">
                        <button onClick={() => setTagFilter(null)}
                            className={`px-2 py-0.5 text-[10px] rounded-full font-semibold ${!tagFilter ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                            All
                        </button>
                        {allTags.map(t => (
                            <button key={t} onClick={() => setTagFilter(t === tagFilter ? null : t)}
                                className={`px-2 py-0.5 text-[10px] rounded-full font-semibold ${t === tagFilter ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                                {t}
                            </button>
                        ))}
                    </div>

                    {/* Preset cards */}
                    {filtered.map(p => (
                        <button key={p.id} onClick={() => setSelectedId(p.id)}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${
                                selectedId === p.id
                                    ? 'border-indigo-400 bg-indigo-50 shadow-sm'
                                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                            }`}>
                            <div className="font-semibold text-sm text-slate-800">{p.name}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{p.description}</div>
                            <div className="flex gap-1 mt-1.5">
                                {p.tags.map(t => (
                                    <span key={t} className="px-1.5 py-0.5 bg-slate-100 text-slate-400 text-[9px] rounded-full">{t}</span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>

                {/* ── Right: Detail & Results ────────────────────────── */}
                <div className="lg:col-span-2 space-y-4">
                    {!selectedId && (
                        <div className="bg-white border rounded-lg p-8 text-center text-slate-400 text-sm">
                            Select a preset to view details and run through the engine
                        </div>
                    )}

                    {selectedId && presetData && (
                        <>
                            {/* Action bar */}
                            <div className="bg-white border rounded-lg p-3 flex flex-wrap items-center gap-2">
                                <button onClick={runPreset} disabled={running}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded hover:bg-emerald-700 disabled:opacity-50">
                                    {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                    Run Through Engine
                                </button>
                                {onLoadIntoAudit && (
                                    <button onClick={() => onLoadIntoAudit(presetData.data)}
                                        className="px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700">
                                        Load into Audit Form
                                    </button>
                                )}
                                <button onClick={copyJson}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded hover:bg-slate-200">
                                    <Copy className="w-3 h-3" />
                                    {copied ? 'Copied!' : 'Copy JSON'}
                                </button>
                            </div>

                            {/* Mutations */}
                            <div className="bg-white border rounded-lg p-3">
                                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Quick Mutations</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {MUTATIONS.map(m => {
                                        const isToggled = !!activeMutations[m.key];
                                        const isEffective = isToggled && !!effectiveMutations[m.key];
                                        const label = isToggled
                                            ? getMutationLabel(m.key, m.label, presetData)
                                            : m.label;
                                        return (
                                            <button key={m.key}
                                                onClick={() => toggleMutation(m.key, m.defaultValue)}
                                                className={`px-2.5 py-1 text-[11px] rounded-full font-medium transition-all ${
                                                    isEffective
                                                        ? 'bg-amber-500 text-white shadow-sm'
                                                        : isToggled
                                                            ? 'bg-slate-300 text-slate-500 line-through'
                                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                                {Object.keys(effectiveMutations).length > 0 && (
                                    <p className="text-[10px] text-amber-600 mt-1.5">
                                        {Object.keys(effectiveMutations).length} effective mutation(s) — click "Run Through Engine" to see effect
                                    </p>
                                )}
                                {Object.keys(activeMutations).length > 0 && Object.keys(effectiveMutations).length === 0 && (
                                    <p className="text-[10px] text-slate-400 mt-1.5">
                                        No effective changes — selected mutations do not alter this preset
                                    </p>
                                )}
                            </div>

                            {/* Custom Overrides */}
                            <div className="bg-white border rounded-lg overflow-hidden">
                                <button onClick={() => setOverridesExpanded(!overridesExpanded)}
                                    className="w-full flex items-center justify-between p-3 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                                    <span className="flex items-center gap-1">
                                        {overridesExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                        Custom Overrides
                                    </span>
                                    {(() => {
                                        const overrideKeys = OVERRIDES.map(o => o.key);
                                        const activeCount = overrideKeys.filter(k => activeMutations[k] !== undefined).length;
                                        return activeCount > 0 ? (
                                            <span className="text-[10px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">
                                                {activeCount} active
                                            </span>
                                        ) : null;
                                    })()}
                                </button>
                                {overridesExpanded && (
                                    <div className="px-3 pb-3 border-t border-slate-100">
                                        <p className="text-[10px] text-slate-400 mt-2 mb-2">
                                            Set precise values for threshold and edge-case testing. Overrides apply after quick mutations.
                                        </p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {OVERRIDES.map(o => {
                                                // Skip add_gifts and add_bpr_asset here if they're already quick mutations
                                                // They share the same key, so the custom value overrides the quick mutation default
                                                const currentVal = activeMutations[o.key];
                                                const isQuickMutationKey = ['add_gifts', 'add_bpr_asset'].includes(o.key);
                                                const quickIsActive = isQuickMutationKey && currentVal !== undefined;
                                                return (
                                                    <div key={o.key} className="flex flex-col">
                                                        <label className="text-[10px] font-semibold text-slate-500 mb-0.5">{o.label}</label>
                                                        <input
                                                            type={o.type === 'date' ? 'text' : 'number'}
                                                            placeholder={o.placeholder}
                                                            value={currentVal ?? ''}
                                                            onChange={e => {
                                                                const raw = e.target.value;
                                                                setActiveMutations(prev => {
                                                                    const next = { ...prev };
                                                                    if (raw === '' || raw === undefined) {
                                                                        delete next[o.key];
                                                                    } else if (o.type === 'date') {
                                                                        next[o.key] = raw;
                                                                    } else {
                                                                        const num = Number(raw);
                                                                        if (!isNaN(num)) next[o.key] = num;
                                                                    }
                                                                    return next;
                                                                });
                                                            }}
                                                            className={`px-2 py-1 text-xs border rounded ${
                                                                currentVal !== undefined
                                                                    ? 'border-violet-300 bg-violet-50'
                                                                    : 'border-slate-200 bg-white'
                                                            } focus:outline-none focus:border-violet-400`}
                                                        />
                                                        {quickIsActive && (
                                                            <span className="text-[9px] text-violet-500 mt-0.5">
                                                                Replaces quick mutation default
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {OVERRIDES.some(o => activeMutations[o.key] !== undefined) && (
                                            <button
                                                onClick={() => setActiveMutations(prev => {
                                                    const next = { ...prev };
                                                    for (const o of OVERRIDES) delete next[o.key];
                                                    return next;
                                                })}
                                                className="mt-2 text-[10px] text-violet-600 hover:text-violet-800 font-semibold"
                                            >
                                                Reset all overrides
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* JSON preview */}
                            <div className="bg-white border rounded-lg overflow-hidden">
                                <button onClick={() => setExpandedJson(!expandedJson)}
                                    className="w-full flex items-center gap-1 p-3 text-xs font-semibold text-slate-500 hover:bg-slate-50">
                                    {expandedJson ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                    JSON Preview
                                </button>
                                {expandedJson && (
                                    <pre className="p-3 pt-0 text-[10px] text-slate-600 overflow-x-auto max-h-80 bg-slate-50 border-t">
                                        {JSON.stringify(presetData.data, null, 2)}
                                    </pre>
                                )}
                            </div>

                            {/* Results */}
                            {runResult && (
                                <div className="bg-white border rounded-lg p-4 space-y-4">
                                    {/* Header */}
                                    <div className="flex items-center gap-3">
                                        <h3 className="font-bold text-sm text-slate-800">Engine Result</h3>
                                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                                            runResult.result.status === 'confirmed'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {runResult.result.status}
                                        </span>
                                    </div>

                                    {/* Tax summary */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <SummaryCard label="Total IHT" value={runResult.result.total_tax_due} highlight />
                                        <SummaryCard label="Estate Tax" value={runResult.result.breakdown?.estate_tax} />
                                        <SummaryCard label="Gift Tax" value={runResult.result.breakdown?.gift_tax_after_taper} />
                                        <SummaryCard label="Taxable Estate" value={runResult.result.breakdown?.taxable_value} />
                                    </div>

                                    {/* Reliefs */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <SummaryCard label="NRB" value={runResult.result.breakdown?.nrb_available} color="blue" />
                                        <SummaryCard label="RNRB" value={runResult.result.breakdown?.rnrb_available} color="blue" />
                                        <SummaryCard label="Spouse Exempt" value={runResult.result.breakdown?.spouse_exemption_total} color="emerald" />
                                        <SummaryCard label="Charity Exempt" value={runResult.result.breakdown?.charity_exemption_total} color="emerald" />
                                    </div>

                                    {runResult.result.breakdown?.bpr_relief_total || runResult.result.breakdown?.apr_relief_total ? (
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            <SummaryCard label="BPR Relief" value={runResult.result.breakdown?.bpr_relief_total} color="purple" />
                                            <SummaryCard label="APR Relief" value={runResult.result.breakdown?.apr_relief_total} color="purple" />
                                        </div>
                                    ) : null}

                                    {/* Asset Beneficiary Breakdown */}
                                    {runResult.estateData?.assets && (
                                        <AssetBeneficiaryBreakdown assets={runResult.estateData.assets} profile={runResult.estateData.profile} />
                                    )}

                                    {/* Blockers */}
                                    {runResult.result.blockers?.length > 0 && (
                                        <div className="bg-amber-50 border border-amber-200 rounded p-2.5">
                                            <div className="text-[10px] font-bold text-amber-600 uppercase mb-1">Blockers</div>
                                            {runResult.result.blockers.map((b, i) => (
                                                <div key={i} className="text-xs text-amber-800">{b}</div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Scenario Mismatch Warning */}
                                    <ScenarioMismatchWarning mismatch={scenarioMismatch} />

                                    {/* Expected vs Actual */}
                                    {runResult.checks && (
                                        runResult.checks.invalidated ? (
                                            // Mutations active — preset expectations may not apply
                                            <div className="border rounded p-3 bg-slate-50 border-slate-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <AlertCircle className="w-4 h-4 text-slate-400" />
                                                    <span className="text-xs font-bold text-slate-500">
                                                        Preset checks paused — mutations active
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-slate-500 mb-2">
                                                    {runResult.checks.invalidReason || 'Mutations have changed the scenario — preset expectations may no longer apply.'}
                                                </p>
                                                <details className="text-[10px]">
                                                    <summary className="text-slate-400 cursor-pointer hover:text-slate-600">
                                                        Show base preset comparison (informational only)
                                                    </summary>
                                                    <table className="w-full text-xs mt-1.5">
                                                        <thead>
                                                            <tr className="text-left text-slate-400">
                                                                <th className="pb-0.5 font-semibold">Field</th>
                                                                <th className="pb-0.5 font-semibold text-right">Base Expected</th>
                                                                <th className="pb-0.5 font-semibold text-right">Mutated Actual</th>
                                                                <th className="pb-0.5 font-semibold text-center">Match</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {runResult.checks.checks.map((c, i) => (
                                                                <tr key={i} className="opacity-60">
                                                                    <td className="py-0.5 text-slate-500">{c.field}</td>
                                                                    <td className="py-0.5 text-right font-mono text-slate-400">{fmt(c.expected)}</td>
                                                                    <td className="py-0.5 text-right font-mono text-slate-600">{fmt(c.actual)}</td>
                                                                    <td className="py-0.5 text-center">
                                                                        {c.passed
                                                                            ? <CheckCircle2 className="w-3.5 h-3.5 text-slate-300 inline" />
                                                                            : <span className="text-slate-400 text-[10px]">—</span>}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </details>
                                            </div>
                                        ) : (
                                            // No mutations — normal preset check display
                                            <div className={`border rounded p-3 ${runResult.checks.allPassed ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    {runResult.checks.allPassed
                                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                                        : <XCircle className="w-4 h-4 text-red-600" />}
                                                    <span className={`text-xs font-bold ${runResult.checks.allPassed ? 'text-emerald-700' : 'text-red-700'}`}>
                                                        {runResult.checks.allPassed ? 'All checks passed' : 'Some checks failed'}
                                                    </span>
                                                </div>
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="text-left text-slate-400">
                                                            <th className="pb-0.5 font-semibold">Field</th>
                                                            <th className="pb-0.5 font-semibold text-right">Expected</th>
                                                            <th className="pb-0.5 font-semibold text-right">Actual</th>
                                                            <th className="pb-0.5 font-semibold text-center">Result</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {runResult.checks.checks.map((c, i) => (
                                                            <tr key={i} className={c.passed ? '' : 'bg-red-100'}>
                                                                <td className="py-0.5 text-slate-700">{c.field}</td>
                                                                <td className="py-0.5 text-right font-mono">{fmt(c.expected)}</td>
                                                                <td className="py-0.5 text-right font-mono">{fmt(c.actual)}</td>
                                                                <td className="py-0.5 text-center">
                                                                    {c.passed
                                                                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 inline" />
                                                                        : <XCircle className="w-3.5 h-3.5 text-red-500 inline" />}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )
                                    )}

                                    {/* Audit Trail */}
                                    {runResult.result.audit_trail && runResult.result.audit_trail.length > 0 && (
                                        <div>
                                            <button onClick={() => setExpandedAudit(!expandedAudit)}
                                                className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 uppercase tracking-wide mb-2">
                                                {expandedAudit ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                                Audit Trail ({runResult.result.audit_trail.length} steps)
                                            </button>
                                            {expandedAudit && (
                                                <table className="w-full text-xs border-collapse">
                                                    <thead>
                                                        <tr className="text-left text-[10px] text-slate-400 border-b">
                                                            <th className="pb-1 font-semibold">Step</th>
                                                            <th className="pb-1 font-semibold text-right">Value</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {runResult.result.audit_trail.map((s, i) => {
                                                            const isTotal = s.step === 'Total IHT due';
                                                            const isDeduction = s.value !== null && s.value < 0;
                                                            return (
                                                                <tr key={i} className={isTotal ? 'border-t-2 border-indigo-200 bg-indigo-50' : 'border-b border-slate-50'}>
                                                                    <td className={`py-1 pr-2 ${isTotal ? 'font-bold text-slate-800' : 'text-slate-600'}`}>
                                                                        {s.step}
                                                                        <span className="block text-[10px] text-slate-400 leading-tight">
                                                                            {s.description}
                                                                            {s.formula && <span className="ml-1 text-indigo-400">({s.formula})</span>}
                                                                        </span>
                                                                    </td>
                                                                    <td className={`py-1 text-right font-mono whitespace-nowrap ${
                                                                        isTotal ? (s.value === 0 ? 'font-black text-emerald-600' : 'font-black text-red-700')
                                                                            : isDeduction ? 'text-emerald-600' : 'text-slate-700'
                                                                    }`}>
                                                                        {s.value !== null ? fmt(s.value) : '—'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            </>}
        </div>
    );
};

// ── Golden Regression Panel ─────────────────────────────────────────

function GoldenRegressionPanel({
    results, running, expandedId, acceptingId,
    onRun, onToggleExpand, onAccept,
}: {
    results: GoldenRunAll | null;
    running: boolean;
    expandedId: string | null;
    acceptingId: string | null;
    onRun: () => void;
    onToggleExpand: (id: string) => void;
    onAccept: (id: string) => void;
}) {
    if (!results && !running) {
        return (
            <div className="bg-white border rounded-lg p-8 text-center">
                <Shield className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                <h3 className="font-bold text-slate-700 mb-1">Golden Regression Checker</h3>
                <p className="text-xs text-slate-400 mb-4">
                    Compares current engine outputs against stored expected values for {12} key probate scenarios.
                    Detects unintended calculation changes after engine updates.
                </p>
                <button onClick={onRun}
                    className="px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded hover:bg-amber-700">
                    Run Regression Check
                </button>
            </div>
        );
    }

    if (running) {
        return (
            <div className="bg-white border rounded-lg p-8 text-center">
                <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-500">Running golden cases through engine...</p>
            </div>
        );
    }

    if (!results) return null;

    const { summary } = results;
    const allGreen = summary.failed === 0;

    return (
        <div className="space-y-4">
            {/* Summary banner */}
            <div className={`rounded-lg p-4 flex items-center gap-4 ${allGreen ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                {allGreen
                    ? <CheckCircle2 className="w-8 h-8 text-emerald-500 flex-shrink-0" />
                    : <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" />}
                <div>
                    <div className={`font-bold text-sm ${allGreen ? 'text-emerald-800' : 'text-red-800'}`}>
                        {allGreen
                            ? `All ${summary.total} golden cases passed`
                            : `${summary.failed} of ${summary.total} golden cases failed`}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        Tolerance: ±£{summary.tolerance} | {summary.passed} passed, {summary.failed} failed
                    </div>
                </div>
                <button onClick={onRun} className="ml-auto p-2 rounded hover:bg-white/50 text-slate-400 hover:text-slate-600">
                    <RefreshCw className="w-4 h-4" />
                </button>
            </div>

            {/* Case list */}
            {results.results.map(r => (
                <div key={r.id} className={`bg-white border rounded-lg overflow-hidden ${r.allPassed ? 'border-slate-200' : 'border-red-300'}`}>
                    {/* Case header */}
                    <button onClick={() => onToggleExpand(r.id)}
                        className={`w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 ${!r.allPassed ? 'bg-red-50/50' : ''}`}>
                        {r.allPassed
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-slate-800">{r.name}</div>
                            <div className="text-[11px] text-slate-400 truncate">{r.description}</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                r.allPassed ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                            }`}>
                                {r.passedChecks}/{r.totalChecks}
                            </span>
                            {expandedId === r.id ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                        </div>
                    </button>

                    {/* Expanded diff table */}
                    {expandedId === r.id && (
                        <div className="border-t px-3 pb-3">
                            <table className="w-full text-xs mt-2">
                                <thead>
                                    <tr className="text-left text-[10px] text-slate-400 uppercase tracking-wider border-b">
                                        <th className="pb-1 font-semibold">Field</th>
                                        <th className="pb-1 font-semibold text-right">Expected</th>
                                        <th className="pb-1 font-semibold text-right">Actual</th>
                                        <th className="pb-1 font-semibold text-right">Diff</th>
                                        <th className="pb-1 font-semibold text-center w-8"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {r.checks.map((c, i) => (
                                        <tr key={i} className={`border-b border-slate-50 ${c.passed ? '' : 'bg-red-50'}`}>
                                            <td className={`py-1 pr-2 ${c.passed ? 'text-slate-600' : 'text-red-800 font-semibold'}`}>
                                                {formatFieldName(c.field)}
                                            </td>
                                            <td className="py-1 text-right font-mono text-slate-500">{c.expected !== null ? fmt(c.expected) : '—'}</td>
                                            <td className={`py-1 text-right font-mono ${c.passed ? 'text-slate-700' : 'text-red-700 font-bold'}`}>
                                                {c.actual !== null ? fmt(c.actual) : '—'}
                                            </td>
                                            <td className={`py-1 text-right font-mono text-[10px] ${
                                                c.diff === null || c.diff === 0 ? 'text-slate-300'
                                                    : c.diff > 0 ? 'text-red-500' : 'text-emerald-500'
                                            }`}>
                                                {c.diff === null ? '—' : c.diff === 0 ? '0' : (c.diff > 0 ? '+' : '') + fmt(c.diff)}
                                            </td>
                                            <td className="py-1 text-center">
                                                {c.passed
                                                    ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline" />
                                                    : <XCircle className="w-3.5 h-3.5 text-red-500 inline" />}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Accept button — only show for failed cases */}
                            {!r.allPassed && (
                                <div className="mt-3 flex items-center gap-2">
                                    <button onClick={() => onAccept(r.id)} disabled={acceptingId === r.id}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 text-[11px] font-semibold rounded hover:bg-amber-200 disabled:opacity-50">
                                        {acceptingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                        Accept Current as New Golden
                                    </button>
                                    <span className="text-[10px] text-slate-400">Updates in-memory only — edit goldenCases.ts to persist</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatFieldName(field: string): string {
    return field
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .replace('Nrb', 'NRB')
        .replace('Rnrb', 'RNRB')
        .replace('Bpr', 'BPR')
        .replace('Apr', 'APR')
        .replace('Iht', 'IHT');
}

// ── Summary Card ────────────────────────────────────────────────────

function getBeneficiaryLabel(type: string | null | undefined): string {
    if (!type) return '—';
    const labels: Record<string, string> = {
        spouse: 'Spouse / CP', direct_descendants: 'Direct descendants', direct_descendant: 'Direct descendant',
        charity: 'Charity', other: 'Other', mixed: 'Mixed', unknown: 'Unknown',
    };
    return labels[type] || type;
}

function getBeneficiaryColor(type: string | null | undefined): string {
    if (!type) return 'text-slate-400';
    if (type === 'spouse') return 'text-emerald-700 font-semibold';
    if (type === 'direct_descendants' || type === 'direct_descendant') return 'text-blue-700';
    if (type === 'charity') return 'text-purple-700';
    return 'text-slate-600';
}

function AssetBeneficiaryBreakdown({ assets, profile }: { assets: any; profile: any }) {
    const fmtV = (n: number | null | undefined) => n != null ? '£' + Math.round(n).toLocaleString('en-GB') : '—';
    const passedToSpouse = profile?.family?.assets_passed_to_spouse;
    const isMarried = profile?.deceased?.maritalStatus === 'MARRIED_OR_CP';

    type Row = { label: string; type: string; value: string; inherits: string; inheritColor: string };
    const rows: Row[] = [];

    // Properties
    for (const p of assets?.properties || []) {
        const val = p.estimated_value || 0;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        const net = Math.max(0, val - mortgage);
        const share = (p.deceased_share_percent ?? 100) / 100;
        const deceasedVal = net * share;

        let inherits = '—';
        if (p.ownership_type === 'joint_tenants' && p.joint_owner_relationship) {
            inherits = p.joint_owner_relationship;
        } else if (p.beneficiary_allocations?.length) {
            inherits = p.beneficiary_allocations.map((a: any) => `${a.type} (${a.percentage}%)`).join(', ');
        } else if (p.deceased_share_passes_to) {
            inherits = p.deceased_share_passes_to;
        }

        rows.push({
            label: p.label || 'Property',
            type: 'Property',
            value: fmtV(deceasedVal),
            inherits: getBeneficiaryLabel(inherits === '—' ? null : inherits),
            inheritColor: getBeneficiaryColor(
                p.ownership_type === 'joint_tenants' ? p.joint_owner_relationship : p.deceased_share_passes_to
            ),
        });
    }

    // Bank accounts
    for (const b of assets?.bank_accounts || []) {
        const val = b.estimated_value || 0;
        rows.push({
            label: b.label || 'Bank Account',
            type: 'Cash',
            value: fmtV(val),
            inherits: getBeneficiaryLabel(b.beneficiary_type),
            inheritColor: getBeneficiaryColor(b.beneficiary_type),
        });
    }

    // Investments
    for (const i of assets?.investments || []) {
        const val = i.estimated_value || 0;
        rows.push({
            label: i.label || 'Investment',
            type: 'Investment',
            value: fmtV(val),
            inherits: getBeneficiaryLabel(i.beneficiary_type),
            inheritColor: getBeneficiaryColor(i.beneficiary_type),
        });
    }

    // BPR assets
    for (const b of assets?.bpr_assets || []) {
        const val = b.estimated_value || 0;
        rows.push({
            label: b.label || 'BPR Asset',
            type: 'BPR',
            value: fmtV(val),
            inherits: getBeneficiaryLabel(b.beneficiary_type),
            inheritColor: getBeneficiaryColor(b.beneficiary_type),
        });
    }

    // APR assets
    for (const a of assets?.apr_assets || []) {
        const val = a.estimated_value || 0;
        rows.push({
            label: a.label || 'APR Asset',
            type: 'APR',
            value: fmtV(val),
            inherits: getBeneficiaryLabel(a.beneficiary_type),
            inheritColor: getBeneficiaryColor(a.beneficiary_type),
        });
    }

    if (rows.length === 0) return null;

    return (
        <div className="border rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-500 uppercase">Asset Beneficiary Breakdown</span>
                {isMarried && passedToSpouse && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        passedToSpouse === 'yes' ? 'bg-emerald-100 text-emerald-700' :
                        passedToSpouse === 'no' ? 'bg-slate-100 text-slate-600' :
                        'bg-amber-100 text-amber-700'
                    }`}>
                        Profile: assets_passed_to_spouse = {passedToSpouse}
                    </span>
                )}
            </div>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-left text-[10px] text-slate-400 border-b">
                        <th className="px-3 py-1.5 font-semibold">Asset</th>
                        <th className="px-3 py-1.5 font-semibold">Type</th>
                        <th className="px-3 py-1.5 font-semibold text-right">Value</th>
                        <th className="px-3 py-1.5 font-semibold">Inherits to</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="px-3 py-1.5 text-slate-700">{r.label}</td>
                            <td className="px-3 py-1.5 text-slate-500">{r.type}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-700">{r.value}</td>
                            <td className={`px-3 py-1.5 ${r.inheritColor}`}>{r.inherits}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SummaryCard({ label, value, highlight, color }: { label: string; value: any; highlight?: boolean; color?: string }) {
    const v = value ?? 0;
    const c = color || (highlight ? (v > 0 ? 'red' : 'emerald') : 'slate');
    const bg = { red: 'bg-red-50', emerald: 'bg-emerald-50', blue: 'bg-blue-50', purple: 'bg-purple-50', slate: 'bg-slate-50' }[c];
    const text = { red: 'text-red-700', emerald: 'text-emerald-700', blue: 'text-blue-700', purple: 'text-purple-700', slate: 'text-slate-700' }[c];
    return (
        <div className={`${bg} rounded-lg p-2.5 text-center`}>
            <div className="text-[10px] text-slate-400 font-semibold uppercase">{label}</div>
            <div className={`text-sm font-bold font-mono ${text}`}>{fmt(v)}</div>
        </div>
    );
}
